import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';
import * as xlsx from 'xlsx';

import * as billCollectApi from '../../lib/api/billCollect';

const COLLECT_TYPE_LABEL = {
  CASH: '현금',
  NOTE: '어음',
  OFFSET: '상계',
  CARD: '카드',
  ETC: '기타',
};

const fmt = (value) => {
  const n = Number(String(value ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '0';
};

/**
 * 수금/지급 내역 조회.
 *
 * 목록과 엑셀이 **같은 조회 결과**를 쓴다. 엑셀만 따로 뽑으면 화면과 파일의 숫자가 달라진다.
 */
const BillCollectListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();

  // 전체 기간을 열어두면 쌓일수록 느려진다. 최근 3개월을 기본으로 둔다.
  const [strtDt, setStrtDt] = useState(dayjs().subtract(3, 'month'));
  const [endDt, setEndDt] = useState(dayjs());
  const [invTyp, setInvTyp] = useState('');
  const [keyword, setKeyword] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState('N');

  const [rows, setRows] = useState([]);
  const [totalAmt, setTotalAmt] = useState(0);
  const [loading, setLoading] = useState(false);
  const loadedRef = useRef(false);

  const search = useCallback(async () => {
    setLoading(true);
    try {
      const res = await billCollectApi.getCollectList({
        strtDt: strtDt ? strtDt.format('YYYY-MM-DD') : null,
        endDt: endDt ? endDt.format('YYYY-MM-DD') : null,
        invTyp: invTyp || null,
        keyword: keyword || null,
        includeCancelled: includeCancelled === 'Y',
      });
      setRows(res?.data?.list || []);
      setTotalAmt(res?.data?.totalAmt || 0);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message;
      enqueueSnackbar(message || '내역을 불러오지 못했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [strtDt, endDt, invTyp, keyword, includeCancelled, enqueueSnackbar]);

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    search();
  }, [search]);

  const isPurchase = (row) => row.invTyp === '매입';

  const handleExcel = () => {
    if (rows.length === 0) {
      enqueueSnackbar('내려받을 내역이 없습니다.', { variant: 'warning' });
      return;
    }
    // 화면에 보이는 그대로 내보낸다. 컬럼을 따로 만들면 화면과 파일이 어긋난다.
    const data = rows.map((r) => ({
      구분: r.invTyp || '',
      일자: r.collectDt || '',
      금액: Number(r.collectAmt || 0),
      거래처: r.partnerNm || '',
      고객사: r.cstNm || '',
      프로젝트번호: r.prjNo || '',
      계산서번호: r.invNo || '',
      발행액: Number(r.invTot || 0),
      수단: COLLECT_TYPE_LABEL[r.collectTyp] || '',
      계좌: r.accountInfo || '',
      처리자: r.handlerNm || '',
      코멘트: r.remark || '',
      등록자: r.crtNm || '',
      등록일시: r.crtDt || '',
      상태: r.useYn === 'Y' ? '유효' : '취소',
      취소사유: r.useYn === 'Y' ? '' : (r.cancelReason || ''),
    }));

    const sheet = xlsx.utils.json_to_sheet(data);
    const book = xlsx.utils.book_new();
    xlsx.utils.book_append_sheet(book, sheet, '수금지급내역');
    const name = `수금지급내역_${dayjs().format('YYYYMMDD_HHmm')}.xlsx`;
    xlsx.writeFile(book, name);
    enqueueSnackbar('엑셀을 내려받았습니다.', { variant: 'success' });
  };

  const activeCount = useMemo(() => rows.filter((r) => r.useYn === 'Y').length, [rows]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        수금 / 지급 내역
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="시작일"
            value={strtDt}
            onChange={setStrtDt}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
          <DatePicker
            label="종료일"
            value={endDt}
            onChange={setEndDt}
            format="YYYY-MM-DD"
            slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
          />
        </LocalizationProvider>
        <TextField
          select label="구분" size="small" value={invTyp}
          onChange={(e) => setInvTyp(e.target.value)} sx={{ width: 110 }}
        >
          <MenuItem value="">전체</MenuItem>
          <MenuItem value="매출">수금(매출)</MenuItem>
          <MenuItem value="매입">지급(매입)</MenuItem>
        </TextField>
        <TextField
          select label="취소분" size="small" value={includeCancelled}
          onChange={(e) => setIncludeCancelled(e.target.value)} sx={{ width: 110 }}
        >
          <MenuItem value="N">제외</MenuItem>
          <MenuItem value="Y">포함</MenuItem>
        </TextField>
        <TextField
          label="검색" size="small" placeholder="거래처·고객사·계산서번호·처리자·계좌"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
          sx={{ flex: '1 1 240px', minWidth: 200 }}
        />
        <Button variant="contained" onClick={search} disabled={loading}>조회</Button>
        <Button variant="outlined" onClick={handleExcel} disabled={loading || rows.length === 0}>
          엑셀 다운로드
        </Button>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {rows.length}건 (유효 {activeCount}건)
        </Typography>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'primary.main' }}>
          합계 {fmt(totalAmt)}
        </Typography>
        {includeCancelled === 'Y' && (
          <Typography variant="caption" color="text.secondary">
            * 합계에는 취소분도 포함됩니다
          </Typography>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress size={26} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '65vh', overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {['구분', '일자', '금액', '거래처', '고객사', '계산서번호',
                  '수단', '계좌', '처리자', '코멘트', '등록자'].map((h) => (
                  <TableCell key={h} align={h === '금액' ? 'right' : 'left'} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center" sx={{ py: 5, color: 'text.disabled' }}>
                    조회된 내역이 없습니다.
                  </TableCell>
                </TableRow>
              ) : rows.map((r) => {
                const cancelled = r.useYn !== 'Y';
                return (
                  <TableRow key={r.collectUuid} hover sx={cancelled ? { backgroundColor: 'action.hover' } : undefined}>
                    <TableCell>
                      <Chip
                        label={isPurchase(r) ? '지급' : '수금'}
                        size="small" variant="outlined"
                        color={isPurchase(r) ? 'warning' : 'primary'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={cancelled ? { textDecoration: 'line-through' } : undefined}>
                      {r.collectDt}
                    </TableCell>
                    <TableCell align="right" sx={cancelled ? { textDecoration: 'line-through' } : { fontWeight: 600 }}>
                      {fmt(r.collectAmt)}
                    </TableCell>
                    <TableCell>{r.partnerNm || '-'}</TableCell>
                    <TableCell>{r.cstNm || '-'}</TableCell>
                    <TableCell>{r.invNo || '-'}</TableCell>
                    <TableCell>{COLLECT_TYPE_LABEL[r.collectTyp] || '-'}</TableCell>
                    <TableCell>{r.accountInfo || '-'}</TableCell>
                    <TableCell>{r.handlerNm || '-'}</TableCell>
                    <TableCell>
                      {cancelled ? (
                        <Typography variant="caption" color="text.secondary">
                          취소 · {r.cancelReason}
                        </Typography>
                      ) : (r.remark || '-')}
                    </TableCell>
                    <TableCell>{r.crtNm || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default BillCollectListContainer;
